import { useEffect } from 'react'

import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  campaignDetailsViewedEvent,
  locationEvent,
  pageViewEvent,
} from 'src/Helpers/TagManager'
import { pageInfo } from 'src/Helpers/TagManager/pageInfo'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import {
  CampaignPlatformTypeEnum,
  CampaignTypeEnum,
  RelaunchInvokePlacement,
} from 'src/Types/campaigns'
import { useRelaunchEvent } from 'src/Hooks/useRelaunchEvent'
import { useRelaunchCampaignMutation } from 'src/Redux/relaunchCampaignApi'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { useUpsellButtonEvent } from 'src/Hooks/useUpsellButtonEvent'
import { useSpotifyCampaignDetails } from 'src/Redux/campaignDetailsApi/useSpotifyCampaignDetails'
import { getAmountOfCampaigns } from 'src/Redux/campaignList-process'

export interface UseSpotifyCampaignDetailsPage {
  onRelaunchCampaign: (campaignId: number) => void
  onUpsellCampaign: (trackName: string) => void
}

export const useSpotifyCampaignDetailsPage =
  (): UseSpotifyCampaignDetailsPage => {
    const userId = useSelector(getUserId)
    const navigate = useNavigate()
    const { handleRelaunchEvent } = useRelaunchEvent()
    const [createRelaunchCampaign] = useRelaunchCampaignMutation()
    const { onDetailsTikTokUpsellButtonClickEvent } = useUpsellButtonEvent()
    const { campaignDetails } = useSpotifyCampaignDetails()
    const amountOfCampaigns = useSelector(getAmountOfCampaigns)
    const isStatusForViewEvent =
      campaignDetails?.status === 'running' ||
      campaignDetails?.status === 'ended' ||
      campaignDetails?.status === 'stopped'

    useEffect(() => {
      if (userId) {
        pageViewEvent({
          userID: userId,
          currentPageInfo: pageInfo.campaignResults,
        })
        locationEvent()
      }
    }, [userId])

    useEffect(() => {
      if (isStatusForViewEvent && userId && campaignDetails) {
        campaignDetailsViewedEvent({
          userId,
          campaignId: campaignDetails?.id,
          platformName: CampaignPlatformTypeEnum.SPOTIFY,
          campaignNumber: amountOfCampaigns,
          campaignType: (campaignDetails?.type || 'basic') as CampaignTypeEnum,
          amountPaid: Number(campaignDetails?.budget) || 1,
          campaignStartDate: campaignDetails?.start_date,
          campaignEndDate: campaignDetails?.end_date,
          campaignStatus: campaignDetails?.status,
          reviews: campaignDetails?.reviews_count,
          placements: campaignDetails?.playlists_adds,
        })
      }
    }, [isStatusForViewEvent, amountOfCampaigns, campaignDetails, userId])

    const onRelaunchCampaign = (campaignId: number | string): void => {
      createRelaunchCampaign(Number(campaignId))
        .unwrap()
        .then((response) => {
          handleRelaunchEvent({
            campaignId: response.id,
            placement: RelaunchInvokePlacement.DETAILS,
          })
          navigate(`/campaigns/schedule/${response.id}`)
          return response
        })
    }

    const onUpsellCampaign = (trackName: string): void => {
      const tiktokCreateLink = `${ArtistRoutesEnum.CampaignsList}${ArtistRoutesEnum.New}/${
        CampaignPlatformTypeEnum.TIKTOK
      }?trackName=${encodeURIComponent(trackName || '')}`

      navigate(tiktokCreateLink)
      onDetailsTikTokUpsellButtonClickEvent()
    }

    return { onRelaunchCampaign, onUpsellCampaign }
  }
